import React, { useState, useEffect, useMemo } from "react";
import {
  Heart,
  Users,
  Utensils,
  CheckCircle,
  Calendar,
  MapPin,
  Lock,
  Search,
  Mail,
  Key,
  Camera,
} from "lucide-react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInAnonymously,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  query,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";

// --- Types ---
interface RSVP {
  id: string;
  firstName: string;
  lastName: string;
  guestCount: number;
  allergies: string;
  email: string;
  timestamp?: { seconds: number; nanoseconds: number } | null;
}

interface RSVPFormData {
  firstName: string;
  lastName: string;
  guestCount: number;
  allergies: string;
  email: string;
}

// --- CONFIGURAZIONE FOTO ---
// SOSTITUISCI QUESTI URL CON I PERCORSI DELLE FOTO REALI DEGLI SPOSI
// Esempio: se le metti nella cartella 'public', usa: ["/foto1.jpg", "/foto2.jpg"]
const couplePhotos = [
  "/foto1.jpg",
  "/foto2.jpg",
  "/foto3.jpg",
  "/foto4.jpg",
  "/foto5.jpg",
  "/foto6.jpg",
  "/foto7.jpg",
  "/foto8.jpg",
];

// --- Firebase Configuration (from environment variables) ---
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// --- CSS STYLES ---
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Great+Vibes&display=swap');

  :root {
    --color-bg: #FDFBF7;
    --color-text: #5C5470; 
    --color-accent: #9CAF88;
    --color-accent-dark: #6B8E23; 
    --color-rose: #D4A5A5;
    --color-lavender: #B5A3C9;
    --color-white: #FFFFFF;
    --color-border: #E7E5E4;
    
    --font-serif: 'Playfair Display', serif;
    --font-script: 'Great Vibes', cursive;
    --font-sans: 'Lato', sans-serif;
  }

  body {
    background-color: var(--color-bg);
    color: var(--color-text);
    font-family: var(--font-sans);
    margin: 0;
    min-height: 100vh;
  }

  /* Banner decorativo floreale in alto */
  .floral-banner {
    width: 100%;
    height: 200px; 
    background-image: url('/invito-bg.png'); 
    background-size: cover;
    background-position: top center;
    background-repeat: no-repeat;
  }

  .app-wrapper {
    max-width: 600px; 
    margin: 0 auto;
    position: relative;
    padding: 0 1.5rem 1.5rem 1.5rem;
    box-sizing: border-box;
    background-color: var(--color-bg);
  }

  .container {
    width: 100%;
    margin: 0 auto;
  }

  .hero {
    text-align: center;
    padding: 1rem 0 2rem 0;
  }

  .hero-names {
    font-family: var(--font-script);
    font-size: 4rem;
    color: #6A4C93;
    margin: 0;
    line-height: 1.1;
  }

  .hero-subtitle {
    font-family: var(--font-serif);
    font-size: 1.1rem;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: var(--color-accent);
    margin-top: 0.5rem;
    margin-bottom: 2rem;
  }

  .info-row {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    font-weight: 500;
    margin-bottom: 3rem;
    color: var(--color-text);
  }

  .info-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: var(--font-serif);
    font-size: 1.1rem;
  }

  /* --- NUOVO CAROSELLO FOTO CSS --- */
  .carousel-section {
    margin-bottom: 3rem;
    text-align: center;
  }

  .carousel-title {
    font-family: var(--font-serif);
    font-size: 1.5rem;
    color: var(--color-accent-dark);
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .carousel-container {
    display: flex;
    gap: 1rem;
    overflow-x: auto;
    padding: 1rem 0.5rem;
    /* Abilita lo scorrimento a scatti */
    scroll-snap-type: x mandatory;
    /* Nasconde la barra di scorrimento per un look più pulito */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none;  /* IE 10+ */
  }
  .carousel-container::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }

  .carousel-image-wrapper {
    flex: 0 0 80%; /* Mostra l'80% dell'immagine, lasciando intravedere la successiva */
    scroll-snap-align: center;
    border-radius: 1rem;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
    border: 3px solid white;
    height: 350px; /* Altezza fissa per uniformità */
    position: relative;
  }

  .carousel-image {
    width: 100%;
    height: 100%;
    object-fit: cover; /* Ritaglia l'immagine per riempire lo spazio senza deformare */
  }

  .carousel-hint {
    font-size: 0.85rem;
    color: var(--color-accent);
    font-style: italic;
    margin-top: 0.5rem;
  }

  /* Form Styles */
  .card {
    background: var(--color-white);
    padding: 2rem;
    border-radius: 1rem;
    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
    border: 1px solid var(--color-border);
    margin-bottom: 3rem;
  }

  .form-group { margin-bottom: 1.5rem; }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.5rem;
    color: var(--color-accent-dark);
  }

  .input, .select, .textarea {
    width: 100%;
    padding: 0.85rem 1rem;
    border-radius: 0.5rem;
    border: 1px solid var(--color-border);
    background-color: #FAFAF9;
    font-family: inherit;
    box-sizing: border-box;
    outline: none;
    transition: all 0.3s ease;
    font-size: 1rem;
  }

  .input:focus, .textarea:focus {
    border-color: var(--color-lavender);
    background-color: #FFF;
    box-shadow: 0 0 0 4px rgba(181, 163, 201, 0.2);
  }

  .btn-primary {
    width: 100%;
    background-color: var(--color-accent);
    color: var(--color-white);
    padding: 1rem;
    border-radius: 2rem; 
    border: none;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.2s;
    font-family: var(--font-sans);
  }
  
  .btn-primary:hover {
    background-color: var(--color-accent-dark);
    transform: translateY(-2px);
    box-shadow: 0 6px 15px -2px rgba(156, 175, 136, 0.4);
  }

  .footer {
    text-align: center;
    padding-top: 2rem;
    margin-top: auto;
    color: #9CA3AF;
    font-size: 0.8rem;
    border-top: 1px dashed #E7E5E4;
  }

  .admin-btn {
    background: none;
    border: none;
    color: #D1D5DB;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin: 1rem auto;
    transition: color 0.2s;
  }
  
  .admin-btn:hover { color: var(--color-accent); }

  /* Admin Dashboard Styles */
  .dashboard-wrapper {
    background: #FAFAF9;
    min-height: 100vh;
  }
  
  .dashboard-nav {
    background: white;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--color-border);
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  .stat-card {
    background: white;
    padding: 1.5rem;
    border-radius: 0.75rem;
    border: 1px solid var(--color-border);
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  }

  .table-container {
    background: white;
    border-radius: 0.75rem;
    border: 1px solid var(--color-border);
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  }
  
  table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
  
  th {
    background: #F3F4F6;
    text-align: left;
    padding: 1rem 1.5rem;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.75rem;
    color: var(--color-text);
    letter-spacing: 0.05em;
  }
  
  td { padding: 1rem 1.5rem; border-bottom: 1px solid #F3F4F6; color: #374151; }
  
  tr:last-child td { border-bottom: none; }
  
  @media (max-width: 640px) {
    .app-wrapper { padding: 0 1rem 1rem 1rem; }
    .hero-names { font-size: 3rem; }
    .form-row { grid-template-columns: 1fr; }
    .floral-banner { height: 150px; }
    .carousel-image-wrapper { height: 280px; /* Un po' più basse su mobile */ }
  }
`;

// --- Main Component ---
export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [view, setView] = useState<"guest" | "admin" | "login">("guest");
  const [rsvps, setRsvps] = useState<RSVP[]>([]);

  // Authentication Setup
  useEffect(() => {
    const initAuth = async () => {
      try {
        await signInAnonymously(auth);
      } catch {
        // Silent fail - auth error will be handled by UI
      }
    };
    initAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Data Fetching
  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, "wedding_rsvps"));

    const unsubscribeSnapshot = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as RSVP[];

        data.sort(
          (a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0)
        );
        setRsvps(data);
      },
      () => {
        // Silent fail - data fetch errors are non-critical
      }
    );

    return () => unsubscribeSnapshot();
  }, [user]);

  // Handle new RSVP submission
  const handleRSVPSubmit = async (formData: RSVPFormData) => {
    if (!user) return false;
    try {
      await addDoc(collection(db, "wedding_rsvps"), {
        ...formData,
        timestamp: serverTimestamp(),
      });
      return true;
    } catch {
      return false;
    }
  };

  const renderView = () => {
    switch (view) {
      case "admin":
        return <AdminDashboard rsvps={rsvps} goBack={() => setView("guest")} />;
      case "login":
        return (
          <AdminLogin
            onSuccess={() => setView("admin")}
            onCancel={() => setView("guest")}
          />
        );
      case "guest":
      default:
        return (
          <GuestLanding
            onSubmit={handleRSVPSubmit}
            onAdminClick={() => setView("login")}
          />
        );
    }
  };

  // Se siamo in admin, non mostriamo il banner floreale
  if (view === "admin") {
    return (
      <>
        <style>{styles}</style>
        {renderView()}
      </>
    );
  }

  // Visualizzazione Guest / Login (con banner floreale in alto)
  return (
    <>
      <style>{styles}</style>
      <div className="floral-banner"></div>
      <div className="app-wrapper">{renderView()}</div>
    </>
  );
}

// --- Components ---

// NUOVO COMPONENTE CAROSELLO
function PhotoCarousel() {
  return (
    <section className="carousel-section">
      <h2 className="carousel-title">
        <Camera size={24} color="#9CAF88" /> La Nostra Storia
      </h2>
      <div className="carousel-container">
        {couplePhotos.map((photoUrl, index) => (
          <div key={index} className="carousel-image-wrapper">
            <img
              src={photoUrl}
              alt={`Foto sposi ${index + 1}`}
              className="carousel-image"
              loading="lazy"
            />
          </div>
        ))}
      </div>
      <p className="carousel-hint">Scorri per vedere le foto →</p>
    </section>
  );
}

interface GuestLandingProps {
  onSubmit: (formData: RSVPFormData) => Promise<boolean>;
  onAdminClick: () => void;
}

// Input validation helpers
const validateName = (name: string): boolean => {
  const trimmed = name.trim();
  return (
    trimmed.length >= 2 &&
    trimmed.length <= 50 &&
    /^[a-zA-ZàèéìòùÀÈÉÌÒÙáéíóúÁÉÍÓÚäëïöüÄËÏÖÜ\s'-]+$/.test(trimmed)
  );
};

const validateEmail = (email: string): boolean => {
  const trimmed = email.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) && trimmed.length <= 100;
};

const sanitizeInput = (input: string): string => {
  return input.trim().slice(0, 500);
};

function GuestLanding({ onSubmit, onAdminClick }: GuestLandingProps) {
  const [formData, setFormData] = useState<RSVPFormData>({
    firstName: "",
    lastName: "",
    guestCount: 1,
    allergies: "",
    email: "",
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success">(
    "idle"
  );
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!validateName(formData.firstName)) {
      errors.firstName = "Nome non valido (min 2 caratteri, solo lettere)";
    }
    if (!validateName(formData.lastName)) {
      errors.lastName = "Cognome non valido (min 2 caratteri, solo lettere)";
    }
    if (formData.guestCount < 1 || formData.guestCount > 10) {
      errors.guestCount = "Numero ospiti deve essere tra 1 e 10";
    }
    if (formData.allergies && formData.allergies.length > 500) {
      errors.allergies = "Testo troppo lungo (max 500 caratteri)";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setStatus("submitting");

    // Sanitize inputs before submission
    const sanitizedData: RSVPFormData = {
      firstName: sanitizeInput(formData.firstName),
      lastName: sanitizeInput(formData.lastName),
      guestCount: Math.min(Math.max(1, formData.guestCount), 10),
      allergies: sanitizeInput(formData.allergies),
      email: sanitizeInput(formData.email),
    };

    const success = await onSubmit(sanitizedData);
    if (success) {
      setStatus("success");
      setFormData({
        firstName: "",
        lastName: "",
        guestCount: 1,
        allergies: "",
        email: "",
      });
      setValidationErrors({});
    } else {
      setStatus("idle");
      alert("Si è verificato un errore. Riprova.");
    }
  };

  if (status === "success") {
    return (
      <div
        className="container"
        style={{ textAlign: "center", paddingTop: "4rem" }}
      >
        <div
          style={{
            width: "100px",
            height: "100px",
            background: "#DCFCE7",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 2rem auto",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          }}
        >
          <CheckCircle size={50} color="#16A34A" />
        </div>
        <h2
          className="hero-names"
          style={{ fontSize: "3rem", color: "#16A34A", marginBottom: "1rem" }}
        >
          Grazie!
        </h2>
        <p
          style={{ marginBottom: "2rem", color: "#57534E", fontSize: "1.1rem" }}
        >
          La tua presenza è stata confermata.
          <br />
          Non vediamo l'ora di festeggiare con te!
        </p>
        <button
          onClick={() => setStatus("idle")}
          style={{
            background: "none",
            border: "none",
            color: "#6B8E23",
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          Inserisci un altro invitato
        </button>
      </div>
    );
  }

  return (
    <div className="container">
      <header className="hero">
        <h1 className="hero-names">
          Matteo <span style={{ color: "#D4A5A5" }}>&</span> Silvia
        </h1>
        <div className="hero-subtitle">Ci sposiamo!</div>

        <div className="info-row">
          <div className="info-item">
            <Calendar size={18} color="#D4A5A5" />
            <span>28 Marzo 2026, ore 16:30</span>
          </div>
          <div className="info-item">
            <MapPin size={18} color="#D4A5A5" />
            <span>Cascina Ranverso, Torino</span>
          </div>
        </div>
      </header>

      <main>
        {/* INSERITO QUI IL CAROSELLO FOTO */}
        <PhotoCarousel />

        <div className="card">
          <h2
            style={{
              fontFamily: "var(--font-serif)",
              textAlign: "center",
              marginBottom: "2rem",
              fontSize: "1.5rem",
              fontStyle: "italic",
              color: "#5C5470",
            }}
          >
            Saremo felici di averti con noi
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label className="label">Nome</label>
                <input
                  type="text"
                  required
                  className="input"
                  placeholder="Es. Mario"
                  maxLength={50}
                  value={formData.firstName}
                  onChange={(e) => {
                    setFormData({ ...formData, firstName: e.target.value });
                    if (validationErrors.firstName) {
                      setValidationErrors({
                        ...validationErrors,
                        firstName: "",
                      });
                    }
                  }}
                  style={
                    validationErrors.firstName ? { borderColor: "#E11D48" } : {}
                  }
                />
                {validationErrors.firstName && (
                  <span
                    style={{
                      color: "#E11D48",
                      fontSize: "0.75rem",
                      marginTop: "0.25rem",
                      display: "block",
                    }}
                  >
                    {validationErrors.firstName}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label className="label">Cognome</label>
                <input
                  type="text"
                  required
                  className="input"
                  placeholder="Es. Rossi"
                  maxLength={50}
                  value={formData.lastName}
                  onChange={(e) => {
                    setFormData({ ...formData, lastName: e.target.value });
                    if (validationErrors.lastName) {
                      setValidationErrors({
                        ...validationErrors,
                        lastName: "",
                      });
                    }
                  }}
                  style={
                    validationErrors.lastName ? { borderColor: "#E11D48" } : {}
                  }
                />
                {validationErrors.lastName && (
                  <span
                    style={{
                      color: "#E11D48",
                      fontSize: "0.75rem",
                      marginTop: "0.25rem",
                      display: "block",
                    }}
                  >
                    {validationErrors.lastName}
                  </span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="label">Numero di persone (incluso te)</label>
              <div style={{ position: "relative" }}>
                <Users
                  size={20}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "12px",
                    color: "#9CA3AF",
                  }}
                />
                <input
                  type="number"
                  min={1}
                  max={10}
                  required
                  className="input"
                  style={{ paddingLeft: "2.75rem" }}
                  value={formData.guestCount}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      guestCount: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </div>
            </div>

            <div className="form-group">
              <label className="label">Note o Allergie Alimentari</label>
              <div style={{ position: "relative" }}>
                <Utensils
                  size={20}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "12px",
                    color: "#9CA3AF",
                  }}
                />
                <textarea
                  rows={3}
                  className="textarea"
                  style={{ paddingLeft: "2.75rem", resize: "none" }}
                  placeholder="Es. Vegetariano, intolleranza al lattosio..."
                  value={formData.allergies}
                  onChange={(e) =>
                    setFormData({ ...formData, allergies: e.target.value })
                  }
                ></textarea>
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={status === "submitting"}
            >
              {status === "submitting" ? "Attendi..." : "Conferma Presenza"}
            </button>
          </form>
        </div>
      </main>

      <footer className="footer">
        <p style={{ marginBottom: "0.5rem", fontStyle: "italic" }}>
          È gradita conferma entro il 28 Gennaio 2026
        </p>
        <button onClick={onAdminClick} className="admin-btn">
          <Lock size={12} /> Accesso Sposi
        </button>
      </footer>
    </div>
  );
}

interface AdminLoginProps {
  onSuccess: () => void;
  onCancel: () => void;
}

function AdminLogin({ onSuccess, onCancel }: AdminLoginProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      onSuccess();
    } catch (err: unknown) {
      const errorCode = (err as { code?: string })?.code;
      if (
        errorCode === "auth/invalid-credential" ||
        errorCode === "auth/user-not-found" ||
        errorCode === "auth/wrong-password"
      ) {
        setError("Credenziali errate. Riprova.");
      } else if (errorCode === "auth/too-many-requests") {
        setError("Troppi tentativi. Riprova tra qualche minuto.");
      } else {
        setError("Errore di autenticazione. Riprova.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
      }}
    >
      <div style={{ width: "100%", maxWidth: "400px" }}>
        <h2
          style={{
            textAlign: "center",
            marginBottom: "2rem",
            fontFamily: "var(--font-serif)",
            color: "#5C5470",
          }}
        >
          Area Riservata Sposi
        </h2>
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label className="label">Email</label>
            <div style={{ position: "relative" }}>
              <Mail
                size={18}
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "13px",
                  color: "#9CA3AF",
                }}
              />
              <input
                type="email"
                required
                autoFocus
                className="input"
                style={{ paddingLeft: "2.5rem" }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Inserisci la tua email"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="label">Password</label>
            <div style={{ position: "relative" }}>
              <Key
                size={18}
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "13px",
                  color: "#9CA3AF",
                }}
              />
              <input
                type="password"
                required
                className="input"
                style={{ paddingLeft: "2.5rem" }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                background: "#FFE4E6",
                color: "#E11D48",
                padding: "0.75rem",
                borderRadius: "0.5rem",
                fontSize: "0.875rem",
                marginBottom: "1.5rem",
                textAlign: "center",
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
            <button
              type="button"
              onClick={onCancel}
              className="btn-primary"
              disabled={isLoading}
              style={{
                background: "white",
                color: "#44403C",
                border: "1px solid #E7E5E4",
              }}
            >
              Indietro
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? "Accesso..." : "Accedi"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface AdminDashboardProps {
  rsvps: RSVP[];
  goBack: () => void;
}

function AdminDashboard({ rsvps, goBack }: AdminDashboardProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const handleLogout = async () => {
    try {
      await signOut(auth);
      // Re-authenticate anonymously for guest access
      await signInAnonymously(auth);
      goBack();
    } catch {
      goBack();
    }
  };

  const totalGuests = useMemo(
    () => rsvps.reduce((acc, curr) => acc + (curr.guestCount || 0), 0),
    [rsvps]
  );
  const familiesCount = rsvps.length;
  const allergiesCount = useMemo(
    () => rsvps.filter((r) => r.allergies && r.allergies.trim() !== "").length,
    [rsvps]
  );

  const filteredRsvps = useMemo(() => {
    return rsvps.filter((rsvp) =>
      (rsvp.firstName + " " + rsvp.lastName)
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  }, [rsvps, searchTerm]);

  return (
    <div className="dashboard-wrapper">
      <nav className="dashboard-nav">
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Heart fill="#9CAF88" color="#9CAF88" size={24} />
          <h1
            style={{
              fontFamily: "var(--font-serif)",
              fontSize: "1.25rem",
              fontWeight: "bold",
              margin: 0,
              color: "#5C5470",
            }}
          >
            Matteo & Silvia{" "}
            <span
              style={{ fontWeight: 400, fontSize: "1rem", color: "#9CA3AF" }}
            >
              | Dashboard
            </span>
          </h1>
        </div>
        <button
          onClick={handleLogout}
          style={{
            background: "white",
            border: "1px solid #E5E7EB",
            padding: "0.5rem 1rem",
            borderRadius: "0.375rem",
            cursor: "pointer",
            color: "#4B5563",
            fontWeight: 500,
          }}
        >
          Esci
        </button>
      </nav>

      <div
        className="container"
        style={{ paddingTop: "2rem", maxWidth: "64rem" }}
      >
        {/* Stats Cards */}
        <div className="stats-grid">
          <div
            className="stat-card"
            style={{ borderLeft: "4px solid #F472B6" }}
          >
            <div
              style={{
                padding: "0.75rem",
                background: "#FCE7F3",
                borderRadius: "0.5rem",
                color: "#DB2777",
              }}
            >
              <Users size={24} />
            </div>
            <div>
              <p style={{ fontSize: "0.875rem", color: "#6B7280", margin: 0 }}>
                Totale Invitati
              </p>
              <p
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  margin: 0,
                  color: "#1F2937",
                }}
              >
                {totalGuests}
              </p>
            </div>
          </div>

          <div
            className="stat-card"
            style={{ borderLeft: "4px solid #60A5FA" }}
          >
            <div
              style={{
                padding: "0.75rem",
                background: "#DBEAFE",
                borderRadius: "0.5rem",
                color: "#2563EB",
              }}
            >
              <CheckCircle size={24} />
            </div>
            <div>
              <p style={{ fontSize: "0.875rem", color: "#6B7280", margin: 0 }}>
                Nuclei Familiari
              </p>
              <p
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  margin: 0,
                  color: "#1F2937",
                }}
              >
                {familiesCount}
              </p>
            </div>
          </div>

          <div
            className="stat-card"
            style={{ borderLeft: "4px solid #F59E0B" }}
          >
            <div
              style={{
                padding: "0.75rem",
                background: "#FEF3C7",
                borderRadius: "0.5rem",
                color: "#D97706",
              }}
            >
              <Utensils size={24} />
            </div>
            <div>
              <p style={{ fontSize: "0.875rem", color: "#6B7280", margin: 0 }}>
                Allergie/Note
              </p>
              <p
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  margin: 0,
                  color: "#1F2937",
                }}
              >
                {allergiesCount}
              </p>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="table-container">
          <div
            style={{
              padding: "1.5rem",
              borderBottom: "1px solid #E7E5E4",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1rem",
              background: "#FFF",
            }}
          >
            <h2
              style={{
                fontSize: "1.125rem",
                fontWeight: "bold",
                margin: 0,
                color: "#374151",
              }}
            >
              Lista Partecipanti
            </h2>
            <div style={{ position: "relative" }}>
              <Search
                size={16}
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "10px",
                  color: "#9CA3AF",
                }}
              />
              <input
                type="text"
                placeholder="Cerca per nome..."
                style={{
                  padding: "0.5rem 1rem 0.5rem 2.25rem",
                  borderRadius: "0.5rem",
                  border: "1px solid #E5E7EB",
                  outline: "none",
                  fontSize: "0.875rem",
                  width: "200px",
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Nome e Cognome</th>
                  <th style={{ textAlign: "center" }}>Numero Ospiti</th>
                  <th>Note / Allergie</th>
                </tr>
              </thead>
              <tbody>
                {filteredRsvps.length > 0 ? (
                  filteredRsvps.map((rsvp) => (
                    <tr key={rsvp.id}>
                      <td style={{ fontWeight: 500, color: "#111827" }}>
                        {rsvp.firstName} {rsvp.lastName}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <span
                          style={{
                            background: "#F3F4F6",
                            padding: "0.25rem 0.75rem",
                            borderRadius: "999px",
                            fontWeight: "bold",
                            color: "#374151",
                            fontSize: "0.85rem",
                          }}
                        >
                          {rsvp.guestCount}
                        </span>
                      </td>
                      <td>
                        {rsvp.allergies ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "0.5rem",
                              color: "#B45309",
                              background: "#FFFBEB",
                              padding: "0.5rem",
                              borderRadius: "0.375rem",
                              fontSize: "0.85rem",
                            }}
                          >
                            <Utensils
                              size={14}
                              style={{ marginTop: "2px", flexShrink: 0 }}
                            />
                            <span>{rsvp.allergies}</span>
                          </div>
                        ) : (
                          <span
                            style={{
                              color: "#D1D5DB",
                              fontStyle: "italic",
                              fontSize: "0.85rem",
                            }}
                          >
                            -
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        textAlign: "center",
                        padding: "4rem",
                        color: "#9CA3AF",
                      }}
                    >
                      Nessun ospite trovato con questo nome.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div
            style={{
              padding: "1rem",
              background: "#F9FAFB",
              borderTop: "1px solid #E5E7EC",
              fontSize: "0.75rem",
              color: "#6B7280",
              textAlign: "center",
            }}
          >
            <Lock
              size={10}
              style={{ marginRight: "4px", verticalAlign: "middle" }}
            />
            Area protetta. La cancellazione dei dati è disabilitata per
            prevenire errori.
          </div>
        </div>
      </div>
    </div>
  );
}
